import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Flex, Text, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, FormControl, FormLabel, Input, Textarea, Checkbox, CheckboxGroup } from "@chakra-ui/react";
import CTable from "../../../components/Table/table";
import Select from "react-select";
import { getAllSubjectsRequest } from "../../../modules/admin-modules/subjects/Actions";
import { getStudentsForBulkMessageRequest } from "../../../modules/admin-modules/students/Actions";
import { sendBulkCustomMessageRequest } from "../../../modules/admin-modules/reports/Actions";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { toast } from "react-toastify";

// Optimized checkbox component to prevent unnecessary re-renders
const StudentCheckbox = React.memo(({ studentId, isSelected, onToggle }) => (
  <Checkbox
    key={`checkbox-${studentId}`}
    isChecked={isSelected}
    onChange={(e) => onToggle(studentId, e.target.checked)}
  />
));

const BulkMessagePage = () => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // State management
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [message, setMessage] = useState("");
  const [recipientType, setRecipientType] = useState("parent");
  const [isLoading, setIsLoading] = useState(false);

  // Redux selectors
  const subjects = useSelector((state) => state.subjects.subjects);
  const students = useSelector((state) => state.students.bulkMessageStudents);
  const reportsLoading = useSelector((state) => state.reports.isLoading);
  const studentsLoading = useSelector((state) => state.students.isLoading);
  // Load subjects on component mount
  useEffect(() => {
    dispatch(getAllSubjectsRequest());
  }, [dispatch]);

  // Load students when subject changes
  useEffect(() => {
    if (selectedSubject?.value) {
      dispatch(getStudentsForBulkMessageRequest(selectedSubject.value));
    }
  }, [selectedSubject, dispatch]);

  // Subject options for dropdown
  const subjectOptions = subjects?.map((subject) => ({
    value: subject.id,
    label: subject.name,
  })) || [];

  // Table columns
  const columns = [
    {
      Header: "Select",
      accessor: "select",
    },
    {
      Header: "Student Name",
      accessor: "name",
    },
    {
      Header: "Phone",
      accessor: "phone",
    },
    {
      Header: "Parent Phone",
      accessor: "parent_phone",
    },
  ];

  // Optimized student selection handler
  const handleStudentToggle = useCallback((studentId, isChecked) => {
    setSelectedStudents(prev => {
      if (isChecked) {
        return [...prev, studentId];
      } else {
        return prev.filter(id => id !== studentId);
      }
    });
  }, []);

  // Optimized table data with useMemo
  const tableData = useMemo(() => {
    return students?.map((student) => ({
      ...student,
      select: (
        <StudentCheckbox
          studentId={student.id}
          isSelected={selectedStudents.includes(student.id)}
          onToggle={handleStudentToggle}
        />
      ),
      group_name: student.group?.name || "N/A",
    })) || [];
  }, [students, selectedStudents, handleStudentToggle]);

  // Handle save message
  const handleSaveMessage = useCallback(() => {
    const currentMessage = document.getElementById("message").value.trim();
    
    if (!currentMessage) {
      toast.error("Please enter a message");
      return;
    }

    setMessage(currentMessage);
    toast.success("Message saved successfully!");
  }, []);

  // Optimized handle send message
  const handleSendMessage = useCallback(() => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    const payload = {
      student_ids: selectedStudents,
      message: message.trim(),
      receipent_phone: recipientType,
    };

    dispatch(sendBulkCustomMessageRequest(payload));
    onClose();
    setMessage("");
    setSelectedStudents([]);
  }, [message, selectedStudents, recipientType, dispatch]);

  // Optimized handle select all students
  const handleSelectAll = useCallback((isChecked) => {
    if (isChecked) {
      setSelectedStudents(students?.map(student => student.id) || []);
    } else {
      setSelectedStudents([]);
    }
  }, [students]);

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10000000"
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            Send Bulk Custom Message
          </Text>
          <LogoutMenu />
        </Flex>

        {/* Subject Selection */}
        <Box mb={6}>
          <Text fontSize="16px" fontWeight="500" mb={2}>
            Select Subject
          </Text>
          <Box w="300px">
            <Select
              options={subjectOptions}
              value={selectedSubject}
              onChange={setSelectedSubject}
              placeholder="Choose a subject..."
              isClearable
            />
          </Box>
        </Box>

        {/* Student Selection Summary */}
        {selectedStudents.length > 0 && (
          <Box mb={4} p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
            <Flex justifyContent="space-between" alignItems="center">
              <Text color="orange.800" fontWeight="500">
                {selectedStudents.length} student(s) selected
              </Text>
              <Button
                colorScheme="orange"
                onClick={onOpen}
                disabled={selectedStudents.length === 0}
              >
                Send Bulk Message
              </Button>
            </Flex>
          </Box>
        )}

        {/* Students Table */}
        {selectedSubject && (studentsLoading || (students && students.length > 0)) && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontSize="18px" fontWeight="500">
                Students in {selectedSubject.label}
              </Text>
              <Checkbox
                isChecked={selectedStudents.length === students?.length && students?.length > 0}
                isIndeterminate={selectedStudents.length > 0 && selectedStudents.length < (students?.length || 0)}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                Select All
              </Checkbox>
            </Flex>

            <CTable
              Columns={columns}
              Data={tableData}
              Actions={false}
              currentpage={1}
              setPageNumber={() => {}}
              isLoading={studentsLoading}
              totalPage={1}
            />
          </Box>
        )}

        {/* No students found message */}
        {selectedSubject && students && students.length === 0 && !reportsLoading && (
          <Box p={6} textAlign="center" bg="gray.50" borderRadius="md">
            <Text fontSize="16px" color="gray.600">
              No students found for {selectedSubject.label}
            </Text>
          </Box>
        )}

        {/* Message Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Send Bulk Custom Message</ModalHeader>
            <ModalBody>
              <FormControl mb={4}>
                <FormLabel>Recipient Type</FormLabel>
                <Select
                  value={{ value: recipientType, label: recipientType.charAt(0).toUpperCase() + recipientType.slice(1) }}
                  onChange={(option) => setRecipientType(option.value)}
                  options={[
                    { value: "parent", label: "Parent" },
                    { value: "student", label: "Student" },
                    { value: "both", label: "Both" },
                  ]}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Message</FormLabel>
                <Textarea
                  id="message"
                
                  placeholder="Enter your custom message..."
                  rows={6}
                />
                <Flex mt={2} gap={2}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleSaveMessage}
                  >
                    Save Message
                  </Button>
                  {message && (
                    <Text fontSize="sm" color="green.600" alignSelf="center">
                      ✓ Message saved
                    </Text>
                  )}
                </Flex>
              </FormControl>

              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600">
                  <strong>Recipients:</strong> {selectedStudents.length} student(s)
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Type:</strong> {recipientType.charAt(0).toUpperCase() + recipientType.slice(1)}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <strong>Message:</strong> {message ? "✓ Saved" : "✗ Not saved"}
                </Text>
              </Box>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleSendMessage}
                isLoading={reportsLoading}
                loadingText="Sending..."
                disabled={!message.trim()}
              >
                Send Message
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </Layout>
  );
};

export default BulkMessagePage; 